import { useEffect, useRef, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { findProfileForUser } from "../utils/profileLookup";
import {
  clearStoredAuthSession,
  getDefaultRouteForRole,
  getStoredAuthSession,
} from "../utils/authSession";

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

async function forceRealtimeLogout() {
  clearStoredAuthSession();
  try {
    await signOut(auth);
  } catch {
    // ignore
  }
}

// ─── Module-level session cache ────────────────────────────────────────────
// Shared across all hook instances so navigating between pages does NOT
// re-trigger Firestore reads while the same Firebase user is signed in.
// Cleared on sign-out / force-logout.
let _cachedUid = null;
let _cachedResult = null; // { adminRecord, profile }

function setCachedSession(uid, result) {
  _cachedUid = uid;
  _cachedResult = result;
}

function getCachedSession(uid) {
  if (_cachedUid === uid && _cachedResult) return _cachedResult;
  return null;
}

export function invalidateAuthCache() {
  _cachedUid = null;
  _cachedResult = null;
}

export function primeAuthCache(uid, result) {
  if (!uid) return;
  setCachedSession(uid, result);
}
// ───────────────────────────────────────────────────────────────────────────

export default function useAuthAccess() {
  // auth.currentUser is synchronously available if Firebase has already
  // resolved the auth state (i.e., on revisit / navigation within session).
  // Combined with the module cache, this means zero loading flash on revisit.
  const [state, setState] = useState(() => {
    const currentUser = auth.currentUser;
    const cached = currentUser ? getCachedSession(currentUser.uid) : null;

    if (currentUser && cached) {
      const resolvedRole =
        cached.adminRecord?.role === "admin"
          ? "admin"
          : cached.profile?.role || getStoredAuthSession()?.role || null;
      return {
        loading: false,
        user: currentUser,
        role: resolvedRole,
        profile: cached.profile,
        adminRecord: cached.adminRecord,
      };
    }

    // No cache yet — show loading until onAuthStateChanged resolves
    return {
      loading: true,
      user: currentUser || null,
      role: null,
      profile: null,
      adminRecord: null,
    };
  });

  useEffect(() => {
    const updateState = (nextValues) => {
      setState((current) => {
        const merged = { ...current, ...nextValues };
        const resolvedRole =
          merged.adminRecord?.role === "admin"
            ? "admin"
            : merged.profile?.role || getStoredAuthSession()?.role || null;
        return { ...merged, role: resolvedRole };
      });
    };

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        invalidateAuthCache();
        setState({
          loading: false,
          user: null,
          role: null,
          profile: null,
          adminRecord: null,
        });
        return;
      }

      // ── Serve from cache when the same user is already resolved ──────────
      const cached = getCachedSession(currentUser.uid);
      if (cached) {
        updateState({
          loading: false,
          user: currentUser,
          adminRecord: cached.adminRecord,
          profile: cached.profile,
        });
        return;
      }

      // ── First load: fetch from Firestore ─────────────────────────────────
      setState((current) => ({
        ...current,
        loading: true,
        user: currentUser,
        profile: null,
        adminRecord: null,
      }));

      let nextAdminRecord = null;

      try {
        const adminSnapshot = await getDoc(doc(db, "admins", currentUser.uid));
        nextAdminRecord = adminSnapshot.exists()
          ? { id: adminSnapshot.id, ...adminSnapshot.data() }
          : null;

        const sessionStartedAt = toMillis(getStoredAuthSession()?.sessionStartedAt);
        const adminForcedLogoutAt = toMillis(nextAdminRecord?.forceLogoutAt);

        if (
          adminForcedLogoutAt &&
          (!sessionStartedAt || adminForcedLogoutAt >= sessionStartedAt)
        ) {
          invalidateAuthCache();
          await forceRealtimeLogout();
          setState({ loading: false, user: null, role: null, profile: null, adminRecord: null });
          return;
        }

        updateState({ adminRecord: nextAdminRecord });
      } catch {
        updateState({ adminRecord: null });
      }

      try {
        const savedAuth = getStoredAuthSession();
        const savedPhone =
          savedAuth?.phone || localStorage.getItem("karankiProfilePhone");
        const foundProfile = await findProfileForUser(db, currentUser, savedPhone);

        if (!foundProfile?.id) {
          setCachedSession(currentUser.uid, { adminRecord: nextAdminRecord, profile: foundProfile || null });
          updateState({ profile: foundProfile || null, loading: false });
          return;
        }

        try {
          const snapshot = await getDoc(doc(db, "profiles", foundProfile.id));
          if (!snapshot.exists()) {
            setCachedSession(currentUser.uid, { adminRecord: nextAdminRecord, profile: null });
            updateState({ profile: null, loading: false });
            return;
          }

          const nextProfile = { id: snapshot.id, ...snapshot.data() };
          const sessionStartedAt = toMillis(getStoredAuthSession()?.sessionStartedAt);
          const forcedLogoutAt = toMillis(nextProfile?.forceLogoutAt);

          if (
            forcedLogoutAt &&
            (!sessionStartedAt || forcedLogoutAt >= sessionStartedAt)
          ) {
            invalidateAuthCache();
            await forceRealtimeLogout();
            setState({ loading: false, user: null, role: null, profile: null, adminRecord: null });
            return;
          }

          setCachedSession(currentUser.uid, { adminRecord: nextAdminRecord, profile: nextProfile });
          updateState({ profile: nextProfile, loading: false });
        } catch {
          setCachedSession(currentUser.uid, { adminRecord: nextAdminRecord, profile: foundProfile });
          updateState({ profile: foundProfile, loading: false });
        }
      } catch {
        updateState({ profile: null, loading: false });
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  // ── Mid-session expiry: when the cached profile has a future membershipExpiresAt,
  // schedule a one-shot timer to bust the cache and re-fetch so the UI instantly
  // reflects the downgrade without requiring a page refresh.
  const expiryTimerRef = useRef(null);

  useEffect(() => {
    // Clear any previous timer
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    const profile = state.profile;
    if (!profile || String(profile.membership || "").toLowerCase() !== "premium") return;

    const expiresAtRaw = profile.membershipExpiresAt;
    if (!expiresAtRaw) return;

    const expiresAtMs =
      typeof expiresAtRaw?.toDate === "function"
        ? expiresAtRaw.toDate().getTime()
        : new Date(expiresAtRaw).getTime();

    if (Number.isNaN(expiresAtMs)) return;

    const msUntilExpiry = expiresAtMs - Date.now();

    // Only set the timer if expiry is in the future and within 24 hours
    // (avoids giant timeouts for plans that expire far in the future)
    if (msUntilExpiry <= 0 || msUntilExpiry > 24 * 60 * 60 * 1000) return;

    expiryTimerRef.current = setTimeout(() => {
      // Bust the cache so the next render re-fetches fresh profile data
      invalidateAuthCache();
      setState((current) => ({
        ...current,
        profile: current.profile
          ? { ...current.profile, membership: "free" }
          : current.profile,
      }));
    }, msUntilExpiry + 2000); // +2s buffer for clock drift

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [state.profile]);

  const isBlocked =
    state.profile?.status === "blocked" ||
    state.profile?.status === "suspended" ||
    Boolean(state.profile?.banned);

  return {
    ...state,
    isAuthenticated: Boolean(state.user),
    isBlocked,
    defaultRoute: getDefaultRouteForRole({
      role: state.role,
      profileId: state.profile?.id,
    }),
  };
}
